import React from "react";
import { motion } from "framer-motion";
import { Volume2, Check, ArrowRight } from "lucide-react";

const IMG =
  "https://images.pexels.com/photos/380769/pexels-photo-380769.jpeg?auto=compress&cs=tinysrgb&w=1600";

const POINTS = [
  "Acoustic panels & wall treatments",
  "Acoustic ceilings & baffles",
  "Office soundproofing",
  "Meeting room acoustic improvements",
  "Commercial interior acoustic installations",
];

export default function AcousticsFeature() {
  return (
    <section
      id="acoustics"
      data-testid="acoustics-feature-section"
      className="bg-[#F5EFE5] text-[#2B2420] py-20 md:py-28 border-y border-[#E5DDD0]"
    >
      <div className="px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-stretch">
        {/* Image + floating CTA card */}
        <div className="lg:col-span-7 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="img-zoom-wrap relative aspect-[4/3] md:aspect-[5/4] overflow-hidden bg-[#E8E0D2]"
          >
            <img
              src={IMG}
              alt="Commercial office interior — acoustic installation"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 bg-[#2B2420] text-[#FAF8F4] text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1.5">
              Commercial Specialism
            </div>
          </motion.div>

          {/* Floating CTA card */}
          <motion.div
            data-testid="acoustics-cta-card"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:absolute lg:-bottom-8 lg:-right-8 mt-6 lg:mt-0 bg-white border border-[#E5DDD0] p-6 md:p-7 max-w-sm shadow-[0_24px_48px_-28px_rgba(43,36,32,0.22)] lift-hover"
          >
            <div className="flex items-center gap-2 text-[#B8732C] text-[10px] font-bold uppercase tracking-[0.3em] mb-3">
              <Volume2 className="w-4 h-4" />
              Acoustic Solutions
            </div>
            <h3
              className="font-black text-2xl tracking-tight leading-[1.15]"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Control the sound <br /> of your space.
            </h3>
            <p className="mt-3 text-sm text-[#4D4037] leading-relaxed">
              Commercial acoustic panels, ceilings and sound-control installations for
              offices, meeting rooms and commercial interiors in London.
            </p>
            <a
              href="#quote"
              data-testid="acoustics-cta-button"
              className="mt-5 inline-flex items-center gap-2 bg-[#B8732C] hover:bg-[#9A5D1E] text-white w-full justify-center px-5 py-3.5 text-xs font-bold uppercase tracking-[0.18em] transition-colors"
            >
              Request a Quote <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </motion.div>
        </div>

        {/* Copy */}
        <div className="lg:col-span-5 flex flex-col justify-center lg:pl-4">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#B8732C] mb-5 font-bold">
            <span className="brand-gold-divider" /> Acoustic Solutions
          </div>
          <h2
            className="font-black tracking-tighter leading-[0.95] text-4xl md:text-5xl lg:text-6xl"
            style={{ fontFamily: "Cabinet Grotesk" }}
          >
            Commercial acoustic <br /> installations, London.
          </h2>
          <p className="mt-6 text-[#4D4037] text-base md:text-lg leading-relaxed max-w-md">
            We design and install acoustic treatments that reduce noise, improve
            speech clarity and meet commercial performance standards — from
            single meeting rooms to whole floors.
          </p>

          <ul className="mt-8 space-y-3">
            {POINTS.map((p) => (
              <li
                key={p}
                className="flex items-start gap-3 text-sm md:text-[15px] text-[#2B2420]"
              >
                <span className="mt-0.5 w-5 h-5 shrink-0 bg-[#B8732C] text-white flex items-center justify-center">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
